import { Component, computed, input, signal } from '@angular/core';
import { LineChart } from 'echarts/charts';
import { GridComponent, TooltipComponent } from 'echarts/components';
import type { EChartsCoreOption } from 'echarts/core';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';

echarts.use([LineChart, GridComponent, TooltipComponent, CanvasRenderer]);

export interface ChartSeries {
    name: string;
    data: (number | null)[];
    color: string;
}

@Component({
    selector: 'app-ice-visuals-dashboard-line-chart',
    imports: [NgxEchartsDirective],
    providers: [provideEchartsCore({ echarts })],
    templateUrl: './ice-visuals-dashboard-line-chart.component.html',
    styleUrl: './ice-visuals-dashboard-line-chart.component.scss',
})
export class IceVisualsDashboardLineChartComponent {
    labels = input.required<string[]>();
    series = input.required<ChartSeries[]>();
    loading = input<boolean>(false);
    showArea = input<boolean>(false);

    hiddenSeriesNames = signal<Set<string>>(new Set());

    visibleSeries = computed(() => {
        const allSeries = this.series();
        const hidden = this.hiddenSeriesNames();

        return allSeries.filter((seriesItem) => {
            const hasData = seriesItem.data.some((value) => value !== null);
            const isVisible = !hidden.has(seriesItem.name);
            return hasData && isVisible;
        });
    });

    legendItems = computed(() => {
        const allSeries = this.series();
        const hidden = this.hiddenSeriesNames();

        return allSeries.map((seriesItem) => ({
            name: seriesItem.name,
            color: seriesItem.color,
            isActive: !hidden.has(seriesItem.name),
            hasData: seriesItem.data.some((value) => value !== null),
        }));
    });

    chartOptions = computed<EChartsCoreOption>(() => {
        const labelValues = this.labels();
        const visibleSeriesData = this.visibleSeries();
        const areaEnabled = this.showArea();

        return {
            tooltip: {
                trigger: 'axis',
                backgroundColor: '#1D2939',
                borderColor: '#1D2939',
                borderRadius: 8,
                textStyle: {
                    color: '#FFFFFF',
                    fontSize: 13,
                    fontFamily: 'Lato',
                },
                axisPointer: {
                    type: 'line',
                    lineStyle: {
                        color: '#9CA3AF',
                        width: 1,
                        type: 'dashed',
                    },
                },
            },
            grid: {
                left: 12,
                right: 12,
                top: 24,
                bottom: 40,
                containLabel: true,
            },
            xAxis: {
                type: 'category',
                data: labelValues,
                boundaryGap: false,
                axisLine: {
                    show: true,
                    lineStyle: {
                        color: '#E5E7EB',
                    },
                },
                axisTick: {
                    show: false,
                },
                axisLabel: {
                    color: '#6B7280',
                    fontSize: 11,
                    fontFamily: 'Lato',
                    margin: 12,
                    rotate: 35,
                },
            },
            yAxis: {
                type: 'value',
                axisLine: {
                    show: false,
                },
                axisTick: {
                    show: false,
                },
                splitLine: {
                    lineStyle: {
                        color: '#F3F4F6',
                        type: 'dashed',
                    },
                },
                axisLabel: {
                    color: '#6B7280',
                    fontSize: 12,
                    fontFamily: 'Lato',
                },
            },
            series: visibleSeriesData.map((seriesItem) => ({
                name: seriesItem.name,
                type: 'line',
                data: seriesItem.data,
                smooth: true,
                symbol: 'circle',
                symbolSize: 6,
                showSymbol: false,
                connectNulls: false,
                emphasis: {
                    focus: 'series',
                    itemStyle: {
                        borderColor: seriesItem.color,
                        borderWidth: 2,
                        color: '#FFFFFF',
                    },
                },
                lineStyle: {
                    color: seriesItem.color,
                    width: 2.5,
                },
                itemStyle: {
                    color: seriesItem.color,
                },
                areaStyle: areaEnabled
                    ? {
                          color: {
                              type: 'linear',
                              x: 0,
                              y: 0,
                              x2: 0,
                              y2: 1,
                              colorStops: [
                                  { offset: 0, color: `${seriesItem.color}30` },
                                  { offset: 1, color: `${seriesItem.color}05` },
                              ],
                          },
                      }
                    : undefined,
            })),
            animation: true,
            animationDuration: 800,
            animationEasing: 'cubicInOut',
        };
    });

    toggleSeriesVisibility(seriesName: string): void {
        this.hiddenSeriesNames.update((currentHidden) => {
            const updated = new Set(currentHidden);
            if (updated.has(seriesName)) {
                updated.delete(seriesName);
            } else {
                updated.add(seriesName);
            }
            return updated;
        });
    }
}
